import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import VehicleCard from "@/components/VehicleCard";
import FinanceLegalNote from "@/components/FinanceLegalNote";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowRight } from "lucide-react";

export default function Fuhrpark({ limit }) {
  const { t } = useLang();
  const [vehicles, setVehicles] = useState(null);

  useEffect(() => {
    api
      .get("/vehicles")
      .then(({ data }) => setVehicles(data))
      .catch(() => setVehicles([]));
  }, []);

  const list = limit && vehicles ? vehicles.slice(0, limit) : vehicles;

  return (
    <section id="fuhrpark" className="py-28 px-6 md:px-10 bg-obsidian" data-testid="fuhrpark-section">
      <div className="max-w-[1600px] mx-auto">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
          <div className="max-w-2xl">
            <p className="text-brand-bright text-xs uppercase tracking-[0.3em] mb-4">{t.fuhrpark.overline}</p>
            <h2 className="font-heading font-black uppercase tracking-tighter text-white text-4xl md:text-6xl leading-[0.95]">
              {t.fuhrpark.title}
            </h2>
            <p className="text-zinc-400 mt-5 max-w-lg">{t.fuhrpark.subtitle}</p>
          </div>
        </div>

        {vehicles === null ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <Skeleton key={i} className="h-[360px] w-full bg-white/5" />
            ))}
          </div>
        ) : list.length === 0 ? (
          <p className="text-zinc-400 text-lg py-20 text-center border border-white/10">{t.fuhrpark.empty}</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {list.map((v, i) => (
              <div key={v.id} className={i === 0 && v.featured ? "lg:col-span-2" : ""}>
                <VehicleCard vehicle={v} featured={i === 0 && v.featured} />
              </div>
            ))}
          </div>
        )}

        {limit && vehicles && vehicles.length > limit && (
          <div className="flex justify-center mt-14">
            <Link
              to="/fahrzeuge"
              data-testid="view-all-vehicles"
              className="group inline-flex items-center gap-2 border border-white/25 text-white font-medium uppercase tracking-wider text-sm px-8 py-4 hover:border-brand-light hover:text-brand-bright transition-colors"
            >
              {t.fuhrpark.all}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        )}

        {vehicles && vehicles.length > 0 && <FinanceLegalNote className="mt-10 max-w-4xl" />}
      </div>
    </section>
  );
}
